import React, { useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { useDispatch } from 'react-redux'
import { withTranslation } from 'react-i18next'
import { useQuery } from '@apollo/client'
import dayjs from 'dayjs'
import { Tabs, Table, Tag, Button, Input, Spin, Alert, Tooltip } from 'antd'

import customerActions from '../../redux/customers/actions'
import CustomerNoteFormContainer from './CustomerNote/CustomerNoteFormContainer'
import ShippingAddressesIndexContainer from './ShippingAddresses/ShippingAdressesIndexContainer'
import BusinessAddressIndexContainer from './BusinessAddresses/BusinessAddressIndexContainer'
import OrderBusinessAddressesContainer from './OrderBusinessAddresses/OrderBusinessAddressesContainer'
import MailingAddressIndexContainer from './MailingAddresses/MailingAddressIndexContainer'
import PotentialLeadIndexContainer from './PotentialLeads/PotentialLeadIndexContainer'
import CustomerWalletsIndexContainer from '../CustomerWallets/CustomerWalletsIndexContainer'
import ProfitSharingIndexContainer from './ProfitSharing/ProfitSharingIndexContainer'
import CustomerCompanyProjectsContainer from './CompanyProjects/CustomerCompanyProjectsContainer'
import TruckImageIndexContainer from './TruckImages/TruckImagesIndexContainer'
import CustomerOrderPaymentDetailsListConatiner from './OrderPaymentDetails/OrderPaymentDetailsListConatiner'
import CartContainer from './Cart'
import CustomerLifetimeValueContainer from './CustomerLifetimeValue'

import { CUSTOMER_DETAIL_QUERY } from './detail/queries'
import {
    compactBahtFromSatangs,
    compactBahtFromValue,
    PROJECT_STATUS_LABELS,
    PROJECT_STATUS_COLORS
} from './dashboard/format'
import { SEGMENT_COLORS, SEGMENT_LABELS } from './CustomerLifetimeValue/format'
import { activityDotColor } from '../Companies/detail/format'
import { buildOrderColumns, ORDER_TABLE_PAGINATION } from './detail/orderColumns'
import BoqDetailModal from './detail/BoqDetailModal'
import CustomerDetailWrapper from './detail/customerDetail.style'
import lineLogo from '/images/base/line-logo.png?url'

const { TabPane } = Tabs

const segmentTag = (seg) => {
    const key = seg || 'unsegmented'
    return <Tag color={SEGMENT_COLORS[key]} style={{ margin: 0 }}>CLV: {SEGMENT_LABELS[key]}</Tag>
}

const CustomerInfo = (props) => {
    // The route :id is the customerNo everywhere in this app.
    const { customerId, i18n } = props
    const dispatch = useDispatch()

    const [activeTab, setActiveTab] = useState('overview')
    const [mergeValue, setMergeValue] = useState('')
    const [selectedBoq, setSelectedBoq] = useState(null)

    const { data, loading, error, refetch } = useQuery(CUSTOMER_DETAIL_QUERY, {
        variables: { customerNo: customerId }
    })

    const customer = data && data.customer
    const perf = customer && customer.performance
    const salesRep = customer && customer.personalAssistant

    // Orders newest first; the table paginates, the activity feed takes the top few.
    const allOrders = (customer && customer.orders) || []
    const sortedOrders = useMemo(() => {
        return allOrders
            .slice()
            .sort((a, b) => {
                const ta = a.createdAt && a.createdAt.raw ? new Date(a.createdAt.raw).getTime() : 0
                const tb = b.createdAt && b.createdAt.raw ? new Date(b.createdAt.raw).getTime() : 0
                return tb - ta
            })
    }, [allOrders])

    // Projects are derived from the customer's orders' companyProject (deduped).
    const projects = useMemo(() => {
        const byId = new Map()
        allOrders.forEach((o) => {
            const p = o.companyProject
            if (p && p.id && !byId.has(p.id)) byId.set(p.id, p)
        })
        return Array.from(byId.values())
    }, [allOrders])

    // Outstanding = sum of the distinct projects' unpaid balance (mirrors the
    // Company Detail rollup, which sums CompanyProject#unpaid_price).
    const outstandingBaht = useMemo(() => {
        return projects.reduce((sum, p) => sum + ((p.unpaidPrice && p.unpaidPrice.value) || 0), 0)
    }, [projects])

    // BOQs are reached through the customer's orders (order.boqs); flattened
    // here with the parent order number for links/actions, newest first.
    const boqs = useMemo(() => {
        const list = []
        allOrders.forEach((o) => {
            const nodes = (o.boqs && o.boqs.nodes) || []
            nodes.forEach((b) => list.push({ ...b, orderNumber: o.number }))
        })
        return list.sort((a, b) => {
            const ta = a.createdAt && a.createdAt.raw ? new Date(a.createdAt.raw).getTime() : 0
            const tb = b.createdAt && b.createdAt.raw ? new Date(b.createdAt.raw).getTime() : 0
            return tb - ta
        })
    }, [allOrders])

    // Activity feed: derived from the most recent orders (state changes).
    const activities = useMemo(() => sortedOrders.slice(0, 5).map((o) => ({
        id: o.id,
        text: `ออเดอร์ ${o.number} · ${o.stateLabel || o.state}`,
        when: o.createdAt && o.createdAt.raw ? dayjs(o.createdAt.raw).fromNow() : '',
        dotColor: activityDotColor(o.state)
    })), [sortedOrders])

    const tickets = (customer && customer.ticketsConnection && customer.ticketsConnection.nodes) || []
    const ticketCount = (customer && customer.ticketsConnection && customer.ticketsConnection.totalCount) || tickets.length

    // ----- cell renderers -----
    const money = (satangs) => <span className="num">{compactBahtFromSatangs(satangs)}</span>

    // Same order-table format as the Company detail overview (shared builder).
    const orderColumns = buildOrderColumns()

    const boqColumns = [
        {
            title: 'BOQ',
            key: 'id',
            width: 140,
            render: (b) => (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <a onClick={() => setSelectedBoq(b)} className="num">#{b.id}</a>
                    <span className="cell-secondary">{(b.createdAt && b.createdAt.shortDate) || ''}</span>
                </div>
            )
        },
        {
            title: 'ออเดอร์',
            key: 'order',
            width: 140,
            render: (b) => (
                <Link to={`/inquiries/${b.orderNumber}/items`} className="num">{b.orderNumber}</Link>
            )
        },
        {
            title: 'ไฟล์',
            key: 'files',
            width: 70,
            render: (b) => <span className="num">{(b.boqFiles && b.boqFiles.totalCount) || 0}</span>
        },
        {
            title: 'บริบท',
            dataIndex: 'boqContext',
            ellipsis: true,
            render: (v) => v || <span className="cell-secondary">-</span>
        },
        {
            title: '',
            key: 'action',
            width: 110,
            render: (b) => <a onClick={() => setSelectedBoq(b)}>ดูรายละเอียด</a>
        }
    ]

    const ticketColumns = [
        {
            title: 'Ticket',
            key: 'ticketNo',
            width: 120,
            render: (t) => (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <span className="num">{t.ticketNo || '-'}</span>
                    <span className="cell-secondary">{t.categoryTypeLabel || ''}</span>
                </div>
            )
        },
        {
            title: 'รายละเอียด',
            dataIndex: 'description',
            ellipsis: true,
            render: (v) => v || <span className="cell-secondary">-</span>
        },
        {
            title: 'คำตอบล่าสุด',
            key: 'reply',
            ellipsis: true,
            render: (t) => (t.lastTicketNoteAnswer && t.lastTicketNoteAnswer.note) || <span className="cell-secondary">-</span>
        },
        {
            title: 'สถานะ',
            key: 'state',
            width: 110,
            render: (t) => (t.stateLabel ? <Tag>{t.stateLabel}</Tag> : <span className="cell-secondary">-</span>)
        },
        {
            title: 'อัพเดท',
            key: 'updated',
            width: 100,
            render: (t) => (t.updatedAt && t.updatedAt.shortDate) || '-'
        }
    ]

    // ----- header -----
    const renderHeader = () => {
        const companies = (customer.companies || [])
        const metaBits = []
        metaBits.push(<span key="no" className="num">{customer.customerNo}</span>)
        if (customer.roleName) metaBits.push(<span key="role">{customer.roleName}</span>)
        if (companies.length > 0) {
            metaBits.push(
                <span key="co">
                    สังกัด {companies.map((c, i) => (
                        <React.Fragment key={c.id}>
                            {i > 0 && ', '}
                            <Link to={`/companies/${c.id}`}>{c.name}</Link>
                        </React.Fragment>
                    ))}
                </span>
            )
        }
        if (salesRep && salesRep.fullName) {
            metaBits.push(<span key="rep">เซลล์ผู้ดูแล: {salesRep.fullName}</span>)
        }

        const contactBits = []
        if (customer.phoneNumber) {
            contactBits.push(<span key="phone" className="num">📞 {customer.phoneNumber}</span>)
        }
        if (customer.email) contactBits.push(<span key="email">✉️ {customer.email}</span>)
        if (customer.lineId || customer.lineUrl) {
            const lineInner = (
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                    <img src={lineLogo} width="16" height="16" alt="LINE" />
                    LINE: {customer.lineId || '-'}
                </span>
            )
            contactBits.push(
                customer.lineUrl
                    ? <a key="line" href={customer.lineUrl} target="_blank" rel="noopener noreferrer">{lineInner}</a>
                    : <span key="line">{lineInner}</span>
            )
        }

        return (
            <div className="header-top">
                <div className="header-identity">
                    <div className="avatar">{(customer.fullName || customer.customerNo || '?').trim().charAt(0)}</div>
                    <div className="identity-lines">
                        <div className="customer-name-row">
                            <span className="customer-name">{customer.fullName || customer.customerNo}</span>
                            {perf && segmentTag(perf.clvSegment)}
                        </div>
                        <div className="customer-meta">
                            {metaBits.map((bit, i) => (
                                <React.Fragment key={i}>
                                    {i > 0 && <span className="sep">·</span>}
                                    {bit}
                                </React.Fragment>
                            ))}
                        </div>
                        {contactBits.length > 0 && <div className="contact-line">{contactBits}</div>}
                    </div>
                </div>
                <div className="header-actions">
                    <Link to={`/customers/${customerId}/edit`}><Button>แก้ไข</Button></Link>
                    <Button onClick={() => setActiveTab('customerWallets')}>+ เพิ่ม Wallet</Button>
                    <Button onClick={() => setActiveTab('potentialLead')}>+ เพิ่ม Potential Lead</Button>
                    <Link to={`/customers/${customerId}/orders/new`}>
                        <Button type="primary">+ เพิ่มออเดอร์</Button>
                    </Link>
                </div>
            </div>
        )
    }

    const renderStats = () => {
        const tiles = [
            {
                label: 'มูลค่าลูกค้าตลอดอายุ',
                value: compactBahtFromSatangs(perf.historicalRevenueClvSatangs),
                sub: `จาก ${perf.totalOrderCount} ออเดอร์`
            },
            {
                label: 'Predicted CLV',
                value: compactBahtFromSatangs(perf.predictedClvSatangs),
                color: '#389e0d',
                sub: `กลุ่ม ${SEGMENT_LABELS[perf.clvSegment || 'unsegmented']}`
            },
            {
                label: 'ยอดค้างชำระ',
                value: compactBahtFromValue(outstandingBaht),
                color: '#d48806'
            },
            {
                label: 'ยอดเงินสะสม',
                value: compactBahtFromSatangs(perf.walletBalanceSatangs),
                sub: 'Wallet'
            },
            {
                label: 'ออเดอร์ล่าสุด',
                value: perf.daysSinceLastOrder != null ? `${perf.daysSinceLastOrder} วัน` : '-',
                sub: perf.lastPaidAt ? dayjs(perf.lastPaidAt).format('D MMM YYYY') : null
            }
        ]
        return (
            <div className="stats-grid">
                {tiles.map((t) => (
                    <div className="stat-tile" key={t.label}>
                        <div className="stat-label">{t.label}</div>
                        <div className="stat-value" style={t.color ? { color: t.color } : undefined}>{t.value}</div>
                        {t.sub && <div className="stat-sub">{t.sub}</div>}
                    </div>
                ))}
            </div>
        )
    }

    const handleMerge = () => {
        const trimmed = mergeValue && mergeValue.trim()
        if (!trimmed) return
        dispatch(customerActions.mergeCustomer({ id: customerId, minor_customer_id: trimmed }))
        setMergeValue('')
    }

    const renderOverview = () => (
        <div className="body-grid">
            <div className="body-col">
                <div className="card card--flush">
                    <div className="section-head">
                        <span className="section-title">ออเดอร์ล่าสุด<span className="section-count">{allOrders.length}</span></span>
                    </div>
                    {sortedOrders.length > 0 ? (
                        <Table
                            columns={orderColumns}
                            dataSource={sortedOrders}
                            rowKey="id"
                            size="small"
                            pagination={ORDER_TABLE_PAGINATION}
                            scroll={{ x: 900 }}
                        />
                    ) : (
                        <div className="empty">ยังไม่มีออเดอร์</div>
                    )}
                </div>

                <div className="card card--flush">
                    <div className="section-head">
                        <span className="section-title">BOQ<span className="section-count">{boqs.length}</span></span>
                    </div>
                    {boqs.length > 0 ? (
                        <Table
                            columns={boqColumns}
                            dataSource={boqs}
                            rowKey="id"
                            size="small"
                            pagination={boqs.length > 10 ? { pageSize: 10, size: 'small' } : false}
                            scroll={{ x: 640 }}
                        />
                    ) : (
                        <div className="empty">ยังไม่มี BOQ</div>
                    )}
                </div>

                <div className="card card--flush">
                    <div className="section-head">
                        <span className="section-title">Ticket<span className="section-count">{ticketCount}</span></span>
                    </div>
                    {tickets.length > 0 ? (
                        <Table
                            columns={ticketColumns}
                            dataSource={tickets}
                            rowKey="id"
                            size="small"
                            pagination={false}
                            scroll={{ x: 640 }}
                        />
                    ) : (
                        <div className="empty">ยังไม่มี Ticket</div>
                    )}
                </div>
            </div>

            <div className="body-col">
                <div className="card card--flush">
                    <div className="section-head">
                        <span className="section-title">Wallet</span>
                        <a onClick={() => setActiveTab('customerWallets')}>ประวัติ</a>
                    </div>
                    <div className="wallet-body">
                        <div className="wallet-label">ยอดเงินสะสม</div>
                        <div className="wallet-amount">{compactBahtFromSatangs(perf ? perf.walletBalanceSatangs : 0)}</div>
                    </div>
                </div>

                <div className="card card--flush">
                    <div className="section-head">
                        <span className="section-title">โครงการ<span className="section-count">{projects.length}</span></span>
                        <a onClick={() => setActiveTab('customerCompanyProjects')}>ดูทั้งหมด</a>
                    </div>
                    {projects.length > 0 ? (
                        projects.map((p) => (
                            <div className="project-row" key={p.id}>
                                <div className="project-row-top">
                                    <span className="project-name">{p.name || '-'}</span>
                                    {p.status && (
                                        <Tag color={PROJECT_STATUS_COLORS[p.status]} style={{ margin: 0 }}>
                                            {PROJECT_STATUS_LABELS[p.status]}
                                        </Tag>
                                    )}
                                </div>
                                <div className="project-row-bottom">
                                    <span>มูลค่า <span className="num">{compactBahtFromValue(p.paidPrice && p.paidPrice.value)}</span></span>
                                    {p.unpaidPrice && p.unpaidPrice.value > 0 && (
                                        <span>ค้าง {compactBahtFromValue(p.unpaidPrice.value)}</span>
                                    )}
                                </div>
                            </div>
                        ))
                    ) : (
                        <div className="empty">ยังไม่มีโครงการ</div>
                    )}
                </div>

                <div className="card card--flush">
                    <div className="section-head">
                        <span className="section-title">กิจกรรมล่าสุด</span>
                    </div>
                    {activities.length > 0 ? (
                        <div className="activity-list">
                            {activities.map((a) => (
                                <div className="activity-row" key={a.id}>
                                    <div className="activity-dot" style={{ background: a.dotColor }} />
                                    <div className="activity-body">
                                        <span className="activity-text">{a.text}</span>
                                        <span className="activity-when">{a.when}</span>
                                    </div>
                                </div>
                            ))}
                        </div>
                    ) : (
                        <div className="empty">ยังไม่มีกิจกรรม</div>
                    )}
                </div>

                <div className="card card--flush">
                    <div className="section-head">
                        <span className="section-title">รวมลูกค้า</span>
                    </div>
                    <div className="merge-body">
                        <span className="merge-hint">ย้ายข้อมูลจากรหัสลูกค้าอื่นเข้ามาที่รหัสนี้</span>
                        <div className="merge-row">
                            <Input
                                placeholder="ARxxxxx"
                                value={mergeValue}
                                onChange={(e) => setMergeValue(e.target.value)}
                                onPressEnter={handleMerge}
                            />
                            <Button onClick={handleMerge} disabled={!mergeValue.trim()}>รวม</Button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    )

    if (loading) return <Spin style={{ display: 'block', margin: '80px auto' }} />
    if (error) return <Alert type="error" message={error.message} />
    if (!customer) return <Alert type="warning" message="ไม่พบข้อมูลลูกค้า" />

    return (
        <CustomerDetailWrapper>
            <div className="card card--padded">
                {renderHeader()}
                {perf && renderStats()}
                <Tabs className="detail-tabs" activeKey={activeTab} onChange={setActiveTab}>
                    <TabPane tab={i18n.t('customerForm.customerInfo')} key="overview">
                        {renderOverview()}
                    </TabPane>
                    <TabPane tab={i18n.t('customerForm.clv')} key="customerLifetimeValue">
                        <CustomerLifetimeValueContainer customerId={customerId} />
                    </TabPane>
                    <TabPane tab={i18n.t('customerForm.orderNote')} key="customerNote">
                        <CustomerNoteFormContainer customerId={customerId} />
                    </TabPane>
                    <TabPane tab={i18n.t('customerForm.locationDetail')} key="locationDetail">
                        <ShippingAddressesIndexContainer customerId={customerId} />
                    </TabPane>
                    <TabPane tab={i18n.t('customerForm.taxInvoiceAddress')} key="taxInvoiceAddress">
                        <BusinessAddressIndexContainer customerId={customerId} />
                    </TabPane>
                    <TabPane tab={i18n.t('customerForm.mailingAddress')} key="mailingAddress">
                        <MailingAddressIndexContainer customerId={customerId} />
                    </TabPane>
                    <TabPane tab={i18n.t('customerForm.customerOrderBusinessAddress')} key="orderBusinessAddress">
                        <OrderBusinessAddressesContainer customerId={customerId} />
                    </TabPane>
                    <TabPane tab="Potential Leads" key="potentialLead">
                        <PotentialLeadIndexContainer customerId={customerId} />
                    </TabPane>
                    <TabPane tab={i18n.t('customerForm.customerWallets')} key="customerWallets">
                        <CustomerWalletsIndexContainer customerId={customerId} />
                    </TabPane>
                    <TabPane tab={i18n.t('customerForm.profitSharing')} key="profitSharing">
                        <ProfitSharingIndexContainer customerId={customerId} />
                    </TabPane>
                    <TabPane tab={i18n.t('customerForm.projects')} key="customerCompanyProjects">
                        <CustomerCompanyProjectsContainer customerId={customerId} />
                    </TabPane>
                    <TabPane tab={i18n.t('orderPaymentDetails')} key="orderPaymentDetails">
                        <CustomerOrderPaymentDetailsListConatiner customerId={customerId} />
                    </TabPane>
                    <TabPane tab={i18n.t('customerForm.truckImages')} key="itemTruckImages">
                        <TruckImageIndexContainer customerId={customerId} />
                    </TabPane>
                    <TabPane tab={i18n.t('customerForm.cart')} key="cart">
                        <CartContainer customerId={customerId} />
                    </TabPane>
                </Tabs>
            </div>
            <BoqDetailModal boq={selectedBoq} onClose={() => setSelectedBoq(null)} />
        </CustomerDetailWrapper>
    )
}

export default withTranslation('common')(CustomerInfo)
